/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMantineTheme, Text } from '@mantine/core';
import { RiArrowRightLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import OptionItem from '@fai/friendli-suite/views/layout/product/option-item';
import TeamAvatar from '@fai/ui/components/avatar/team';
import { TextVariants } from '@fai/ui/components/typography/variants';

import type { TeamsList } from './model';

export function SwitchableTeamsSection({ teams, teamId }: TeamsList) {
  const navigate = useNavigate();

  const theme = useMantineTheme();
  const { t } = useTranslation();

  const handleTeamSwitchClick = (targetTeamId: string) => {
    navigate(Paths.team(targetTeamId).default);
  };

  return (
    <>
      <Text
        c={theme.pf.color.fg.neutral.subtle}
        p={`${theme.pf.layout.xs} 0 ${theme.pf.layout['3xs']} ${theme.pf.layout.xs}`}
        variant={TextVariants.Title2Xs}
      >
        {t('layout.switch_team')}
      </Text>

      {teams?.map((team) => (
        <OptionItem
          key={team.id}
          isActive={teamId === team.id}
          isVisible={true}
          label={team.name}
          leftSlot={<TeamAvatar size={20} />}
          value={team.id}
          onClick={() => handleTeamSwitchClick(team.id)}
        />
      ))}
    </>
  );
}

export function AllTeamsOption() {
  const { t } = useTranslation();

  return (
    <OptionItem
      isVisible={true}
      label={t('layout.all_teams')}
      rightSlot={<RiArrowRightLine size={16} />}
      value="teams"
    />
  );
}
